import "./polyfills";
import { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { createBrowserHistory } from "history";
import { get } from "lodash";
import { createRoot } from "react-dom/client";
import { pollAnnouncements } from "./announcement/announcementDomain";
import { ApiService, createAxiosInstance, isAxiosError } from "./api";
import App from "./App";
import "./components-v2/index.css";
import { TooltipManager } from "./components-v2/Tooltip";
import "./cookies/cookies.css";
import { initI18n, toggleCIMode } from "./i18n/i18nDomain";
import "./index.css";
import { handleLinks, rewriteNavigation, startRouter } from "./routes/router";
import { toAppPath, toBrowserPath } from "./routes/companyByIdDomain";
import { MatchedDCRoute, routes } from "./routes/routes";
import { RootStore } from "./stores/RootStore";
import { DispatchContext, RootStoreContext } from "./stores/RootStoreContext";
import { ErrorLocation, ErrorSource, toErrorData } from "./telemetry/errorDomain";
import { isReportableLog, toLogData } from "./telemetry/logDomain";
import { isSamePage, readBrowserInfo, toPageViewData } from "./telemetry/pageViewDomain";
import { isReportableRequestError, toRequestErrorData } from "./telemetry/requestErrorDomain";
import { telemetryEvent } from "./telemetry/telemetryDomain";
import * as updater from "./updater";
import { isBrowserExtensionError, isForeignScriptError } from "./utils/browserExtensions";
import { isDevEnvironment } from "./utils/environments";
import { logDebug, LogLevel, logToConsoleAndSentry, setLogListener } from "./utils/log";

const history = createBrowserHistory();
const axiosInstance = createAxiosInstance();
const apiService = new ApiService(axiosInstance);
const rootStore = new RootStore(axiosInstance, apiService, history);
setLogListener((level, args) => {
  if (isReportableLog(args)) {
    rootStore.dispatch([telemetryEvent("log", toLogData(level, args))]);
  }
});
const handleRouteChange = (route: MatchedDCRoute) => {
  const previous = rootStore.route;
  rootStore.setRoute(route);
  if (!isSamePage(previous, route)) {
    rootStore.dispatch([
      telemetryEvent("page_view", toPageViewData(route, previous, readBrowserInfo())),
    ]);
  }
};

const handleRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  if (config.headers) {
    config.headers["X-Language"] = rootStore.i18n.lang;
    config.headers["X-Correlation-Id"] = rootStore.telemetry.context.correlationId;
  }
  return config;
};

const handleResponse = (response: AxiosResponse) => {
  updater.checkVersion(response.headers["x-version"]);
  return response;
};

const handleResponseError = (err: unknown) => {
  if (isAxiosError(err)) {
    updater.checkVersion(err.response?.headers["x-version"]);
    const status = err.response?.status;
    if (status === 401) {
      rootStore.userStore.handleAuthError(err);
    }
  }
  if (isReportableRequestError(err)) {
    rootStore.dispatch([telemetryEvent("request_error", toRequestErrorData(err))]);
  }
  return Promise.reject(err);
};

const reportUnhandledError = (
  source: ErrorSource,
  error: unknown,
  message: string,
  location: ErrorLocation,
) => {
  rootStore.dispatch([telemetryEvent("error", toErrorData(source, error, message, location))]);
  const sentryMessage = isAxiosError(error) ? `HTTP ${error.status} ${error.config?.url}` : message;
  logToConsoleAndSentry(LogLevel.ERROR, [error, `${source}: ${sentryMessage}`, location]);
};

const handleUncaughtError = (
  source: ErrorSource,
  error: unknown,
  location: ErrorLocation,
  message?: string,
) => {
  const msg = String(message ?? get(error, "message"));
  switch (true) {
    case isBrowserExtensionError(error):
      logDebug("Ignoring error from browser extension", { error });
      break;
    case msg.startsWith("Importing a module script failed"):
    case msg.startsWith("Failed to fetch dynamically imported module"):
    case msg.startsWith("Unable to preload CSS for"):
      logDebug("Failed to load script/css, checking for update", { error });
      void updater.updateAfterChunkError();
      break;
    case isAxiosError(error) && error.status === 401:
      // handled above in handleResponseError
      break;
    default:
      reportUnhandledError(source, error, msg, location);
  }
};

window.onerror = (message, file, line, column, error) => {
  if (!isDevEnvironment && !file?.includes("/assets/")) {
    return;
  }
  handleUncaughtError(
    "window.onerror",
    error,
    { file, line, column },
    typeof message === "string" ? message : undefined,
  );
};

window.onunhandledrejection = (event) => {
  if (!isDevEnvironment && isForeignScriptError(event.reason)) {
    logDebug("Ignoring rejection from a script outside our bundles", { error: event.reason });
    return;
  }
  handleUncaughtError("unhandledrejection", event.reason, {});
};

axiosInstance.interceptors.request.use(handleRequest);
axiosInstance.interceptors.response.use(handleResponse, handleResponseError);

if (import.meta.hot) {
  Object.assign(window, {
    dev: {
      cimode: () => rootStore.dispatch(toggleCIMode()),
      prod: () => {
        sessionStorage.setItem("env", "prod");
        window.location.reload();
      },
      test: () => {
        sessionStorage.setItem("env", "prod");
        window.location.reload();
      },
      dev: () => {
        sessionStorage.removeItem("env");
        window.location.reload();
      },
    },
  });
  logDebug(`====== ENV ${sessionStorage.getItem("env") ?? "dev"} ======`);
}

initI18n(rootStore);
rootStore.userStore.init();
rewriteNavigation(history, (path) => toBrowserPath(path, rootStore.companyIds));
startRouter(history, routes, handleRouteChange, (pathname) =>
  toAppPath(pathname, rootStore.companyIds),
);
handleLinks(history, (href) => /^\/(?!api)/.test(href));
pollAnnouncements(rootStore.dispatch);

// Hack: prevent Safari from showing logged-in page from the cache after logout
addEventListener("pageshow", () => {
  rootStore.userStore.checkAuthStatus();
});

history.listen(() => {
  updater.updateIfNewVersionAvailable();
  rootStore.messageStore.hideMessage();
  rootStore.dialogStore.closeDialog();
});

createRoot(document.getElementById("root")!).render(
  <DispatchContext.Provider value={rootStore.dispatch}>
    <RootStoreContext.Provider value={rootStore}>
      <App
        history={history}
        onUncaughtError={(error, errorInfo) =>
          handleUncaughtError("error_boundary", error, {
            componentStack: errorInfo.componentStack ?? undefined,
          })
        }
      />
      <TooltipManager />
    </RootStoreContext.Provider>
  </DispatchContext.Provider>,
);
